import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Checkbox,
  Chip,
  TableCell,
  TableRow,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import TextShowMore from 'src/components/TextShowMore';
import TCellMessageType from 'src/views/management/ScheduledMessagesView/Results/TCell/TCellMessageType';
import SendingProgress from 'src/views/management/ScheduledMessagesView/Results/TCell/SendingProgress';
import helpers from 'src/helpers';
import {
  STORAGE,
  MESSAGES,
} from 'src/constants';
import MoreAttachmentsBadge from 'src/components/MoreAttachmentsBadge';

const {
  EVENT_SMART_ACTION_MESSAGE,
  SCHEDULED_MESSAGE,
} = MESSAGES.SCHEDULED_MESSAGE_TYPES;

const { VCARD_FILENAME } = MESSAGES;

/**
 * Check if attachment is a Contact Card
 */
function isContactCardAttachment(fileName) {
  return fileName === VCARD_FILENAME;
}

/**
 * Strip datetime prefix from filename (e.g., 2026-01-21-16-08-15-77-file.pdf -> file.pdf)
 * Also handles EO-ID- prefix pattern
 */
function stripDatetimePrefix(fileName) {
  if (!fileName) return '';
  const eoIdPattern = /^EO-ID-\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2,3}-/;
  if (eoIdPattern.test(fileName)) {
    return fileName.replace(eoIdPattern, '');
  }
  const datetimePrefixPattern = /^\d{4}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2}-\d{2,3}-/;
  return fileName.replace(datetimePrefixPattern, '');
}

/**
 * Get file extension from filename
 */
function getFileExtension(fileName) {
  if (!fileName) return '';
  return fileName.toLowerCase().split('.').pop();
}

/**
 * Check if file is an image type
 */
function isImageFile(fileName) {
  const ext = getFileExtension(fileName);
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
}

/**
 * Check if file is an audio type
 */
function isAudioFile(fileName) {
  const ext = getFileExtension(fileName);
  return ['mp3', 'm4a', 'wav', 'ogg', 'aac', 'wma', 'flac'].includes(ext);
}

function TBodyResultsScheduledMessage({
  classes,
  scheduledMessage,
  selectedScheduledMessages,
  handleSelectOneScheduledMessage,
  handleRowClick,
  isShowMoreTextChecked,
  handleClickShowMoreButton,
  userId,
  azAccountName,
  databaseBeingUsed,
  handleRefreshPage,
  query = {},
}) {
  const {
    id,
    messageType,
    sendMethod,
    eventName,
    groupActionName,
    smartActionName,
    // contactsCount,
    messageBody,
    messageAttachment,
    messageAttachmentContainer,
    additionalAttachmentsCount = 0,
    dateTimeToSend,
    contactsToSendTotalCount,
    sentMessagesCount,
    isSending,
    sentAt,
    isStopped = false,
    isProbablySent, // BE doesn't send this field, we calculate it in the reducer based on the Socket event
  } = scheduledMessage || {};

  // console.log('scheduledMessage', scheduledMessage);

  const probablySent = useMemo(
    () => (
      isProbablySent
      || (
        isSending
        && contactsToSendTotalCount <= sentMessagesCount
        && sentMessagesCount > 0
        && ![EVENT_SMART_ACTION_MESSAGE, SCHEDULED_MESSAGE].includes(messageType)
      )
    ),
    [isSending, contactsToSendTotalCount, sentMessagesCount, isProbablySent]
  );

  const isScheduledMessageSelected = selectedScheduledMessages.some(
    (item) => item.id === id && item.type === messageType
  );

  const handleOnClickCheckbox = (event, messageId, type) => {
    handleSelectOneScheduledMessage(event, messageId, type);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOnClickRefresh = useCallback((event) => {
    handleRefreshPage();
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const { status } = query || {};
  const isViewingSent = status === 'sent';

  return id && (
    <TableRow
      key={id}
      className={`${classes.tableRow}${probablySent ? ' little-hidden-1' : ''}`}
      selected={!isViewingSent && isScheduledMessageSelected}
      onClick={(event) => handleRowClick(event, id, messageType)}
      hover
      id={`scheduledMessageId${id}-${messageType}`}
    >
      {!isViewingSent && (
        <TableCell padding="checkbox">
          <Checkbox
            checked={isScheduledMessageSelected}
            onClick={(event) => handleOnClickCheckbox(event, id, messageType)}
            value={isScheduledMessageSelected}
            color="secondary"
          />
        </TableCell>
      )}

      {/* Date & Time to Send */}
      <TableCell className={classes.nowrap}>
        {isViewingSent ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
              {!helpers.isEmpty(dateTimeToSend) ? moment(dateTimeToSend).format('MMM D, YYYY [at] h:mm a') : ''}
            </Box>
            <Box sx={{ color: '#4caf50' }}>
              {!helpers.isEmpty(sentAt) ? moment(sentAt).format('MMM D, YYYY [at] h:mm a') : ''}
            </Box>
            {/* CON-2011 — sending was stopped part way; sentAt is the last send. */}
            {isStopped && (
              <TooltipMUI6 title="Sending was stopped. Only the contacts who were sent this message are counted.">
                <Chip
                  size="small"
                  label="Stopped"
                  color="warning"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                />
              </TooltipMUI6>
            )}
          </Box>
        ) : (
          <>
            {!helpers.isEmpty(dateTimeToSend) ? moment(dateTimeToSend).format('MMM D, YYYY [at] h:mm a') : ''}
            <SendingProgress
              isSending={(isSending || probablySent) && contactsToSendTotalCount > 0}
              sentTo={sentMessagesCount}
              contactsCount={contactsToSendTotalCount}
              isLittleHidden={probablySent}
            />
            {probablySent && (
              <Box
                className="not-hidden"
                sx={{
                  marginTop: '8px',
                }}
              >
                <TooltipMUI6
                  title={(
                    <>
                      Click this button to refresh the sending progress.
                    </>
                  )}
                >
                  <IconButton
                    onClick={handleOnClickRefresh}
                  >
                    <RefreshIcon />
                  </IconButton>
                </TooltipMUI6>
              </Box>
            )}
          </>
        )}
      </TableCell>

      {/* Type of Message */}
      <TableCell>
        <TCellMessageType
          messageType={messageType}
          sendMethod={sendMethod}
          eventName={eventName}
          groupName={groupActionName}
          smartActionName={smartActionName}
        />
      </TableCell>

      {/* Contacts count */}
      <TableCell
        className={classes.contactsCount}
        align="center"
      >
        <Chip
          size="small"
          icon={<GroupIcon />}
          label={contactsToSendTotalCount || 0}
        />
      </TableCell>

      {/* Text Message To Send */}
      <TableCell
        align="left"
        className={classes.textMessages}
      >
        <TextShowMore
          text={messageBody || ''}
          maxTextLength={250}
          checked={isShowMoreTextChecked(id)}
          setChecked={handleClickShowMoreButton}
          orderId={id}
        />
      </TableCell>

      {/* Attachment */}
      <TableCell
        align="left"
        className={classes.image}
      >
        {messageAttachment ? (
          (() => {
            if (isContactCardAttachment(messageAttachment)) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: '#F0F7FF',
                    border: '1px solid #1976D2',
                    borderRadius: '4px',
                    padding: '6px 10px',
                    maxWidth: 140,
                  }}
                >
                  <ContactPageIcon sx={{ color: '#1976D2', fontSize: 24 }} />
                  <Typography variant="body2" fontWeight={500} noWrap>
                    Your Contact Card
                  </Typography>
                </Box>
              );
            }
            if (isImageFile(messageAttachment)) {
              return (
                <img
                  src={helpers.azure.getImageFileURL(messageAttachment, userId, azAccountName, STORAGE.sas, databaseBeingUsed, messageAttachmentContainer, true)}
                  alt={messageAttachment}
                />
              );
            }
            // PDF, audio, or other file type
            const ext = getFileExtension(messageAttachment);
            const displayName = stripDatetimePrefix(messageAttachment);
            const isAudio = isAudioFile(messageAttachment);
            let FileIcon = InsertDriveFileIcon;
            let iconColor = '#757575';
            if (ext === 'pdf') {
              FileIcon = PictureAsPdfIcon;
              iconColor = '#d32f2f';
            } else if (isAudio) {
              FileIcon = AudioFileIcon;
              iconColor = '#7B1FA2';
            }
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  background: '#F5F5F5',
                  border: '1px solid #E0E0E0',
                  borderRadius: '4px',
                  padding: '6px 10px',
                  maxWidth: 160,
                }}
              >
                <FileIcon sx={{ color: iconColor, fontSize: 24 }} />
                <Typography
                  variant="body2"
                  noWrap
                  title={displayName}
                  sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {displayName}
                </Typography>
              </Box>
            );
          })()
        ) : ''}
        {/* CON-1934 — the files behind the first. */}
        {messageAttachment && <MoreAttachmentsBadge count={additionalAttachmentsCount} />}
      </TableCell>
    </TableRow>
  );
}

TBodyResultsScheduledMessage.propTypes = {
  azAccountName: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  databaseBeingUsed: PropTypes.string.isRequired,
  handleClickShowMoreButton: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  handleSelectOneScheduledMessage: PropTypes.func.isRequired,
  handleRefreshPage: PropTypes.func.isRequired,
  isShowMoreTextChecked: PropTypes.func.isRequired,
  scheduledMessage: PropTypes.object.isRequired,
  selectedScheduledMessages: PropTypes.array.isRequired,
  userId: PropTypes.number.isRequired,
  query: PropTypes.object,
};

TBodyResultsScheduledMessage.defaultProps = {};

export default TBodyResultsScheduledMessage;
